import { useEffect, useState } from 'react';
import { Flex, Select, TextInput, type SelectOption } from '@gravity-ui/uikit';
import { useTranslation } from 'react-i18next';
import { useProducts } from './api';
import type { CreateProductRequest, Product } from './types';

type Draft = {
  name: string;
  unit: string;
};

const emptyDraft: Draft = { name: '', unit: '' };

const UNIT_OPTIONS = ['кг', 'г', 'л', 'мл', 'шт', 'уп', 'кор', 'ящ'];

/** Название для сравнения: без регистра, пробелов по краям и двойных пробелов. */
function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Состояние и разметка минимальной формы создания продукта (имя + единица). */
export function useProductForm(initial?: Product | null, resetKey?: string) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const products = useProducts();

  useEffect(() => {
    if (initial) {
      setDraft({ name: initial.name, unit: initial.unit });
      return;
    }
    setDraft(emptyDraft);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetKey задаёт момент предзаполнения
  }, [resetKey]);

  const patch = (field: keyof Draft, value: string) =>
    setDraft((current) => ({ ...current, [field]: value }));

  /** Единица сохраняется как есть: если её нет в списке, добавляем вручную. */
  const unitOptions: SelectOption[] = draft.unit
    ? [...UNIT_OPTIONS, draft.unit]
        .filter((unit, index, all) => all.indexOf(unit) === index)
        .map((unit): SelectOption => ({ value: unit, content: unit }))
    : UNIT_OPTIONS.map((unit): SelectOption => ({ value: unit, content: unit }));

  /** Предзаполнить название (например, тем, что пользователь ввёл и не нашёл). */
  const setInitialName = (value: string) => setDraft((current) => ({ ...current, name: value }));

  const getBody = (): CreateProductRequest => ({
    name: draft.name.trim(),
    unit: draft.unit.trim() || 'шт',
  });

  /** Одноимённых продуктов не допускаем; сам редактируемый продукт не в счёт. */
  const normalized = normalizeName(draft.name);
  const nameTaken =
    normalized !== '' &&
    (products.data?.products ?? []).some(
      (product) => product.id !== initial?.id && normalizeName(product.name) === normalized
    );

  const fields = (
    <Flex direction="column" gap={2}>
      <TextInput
        size="l"
        label={t('products.nameLabel')}
        placeholder={t('products.namePlaceholder')}
        value={draft.name}
        onUpdate={(value) => patch('name', value)}
        validationState={nameTaken ? 'invalid' : undefined}
        errorMessage={t('products.nameTaken')}
        errorPlacement="inside"
        autoFocus
      />
      <Select
        size="l"
        label={t('products.unitLabel')}
        placeholder={t('products.unitPlaceholder')}
        value={draft.unit ? [draft.unit] : []}
        onUpdate={(value) => patch('unit', value[0] ?? '')}
        filterable
        options={unitOptions}
        popupClassName="product-dialog__unit-popup"
      />
    </Flex>
  );

  return { fields, getBody, setInitialName, canSubmit: normalized !== '' && !nameTaken };
}
